/**
 * activityCongrats — ce que la fin d'une activité rendue a rapporté, calculé une fois, sans écran.
 *
 * Extrait de `ActivityScreen.pushSuccessScreen` (l'ancien écran d'activité, à la réponse de
 * `finalize`) : l'XP (`activity.xp_gained`), les pétales gagnés (les compétences qui viennent de
 * passer une étape et dont l'évolution n'est pas encore lue), le chrono (`activity.duration`).
 *
 * Le mot du champion (`celebrationFor`) suit `ActivityFinalizedCongrats` : le champion du jour (ou
 * celui que l'appelant impose : Sam après une activité menée avec lui), la banque à la hauteur de la
 * séance (les activités finies du jour), un mot rare pour la première activité ou un enchaînement
 * rapide.
 */
import { getCelebrationMessage, getChampion, getIsFastCombo, type CelebrationChampion } from "./celebration"

/** Une compétence du jeu, telle que `finalize` la rend (`skill_sets`). */
export type ApiSkillSet = {
    id?: number | null
    current_count?: number | null
    current_step?: number | null
    is_last_evolution_read?: boolean | null
    steps?: ({ title_gained?: string | null; petals_gained?: number | null } | null)[] | null
}

/** La réponse de `POST api/activities/{id}/finalize` (`data`). */
export type ApiFinalizeResult = {
    activity?:
        ({ id?: number | null; xp_gained?: number | null; duration?: number | null } & Record<string, unknown>) | null
    user?: Record<string, unknown> | null
    skill_sets?: ApiSkillSet[] | null
    correction_wait?: { questions?: unknown[] | null } | null
    is_first_of_day?: boolean | null
}

export type ActivityCongrats = {
    xp: number
    petals: number
    durationSeconds: number
}

/**
 * Les quêtes dont l'évolution se marque lue à l'écran de fin (`markSkillSetEvolutionAsRead`) : celles
 * que l'ancien écran recevait (`userSkillSets`, `ActivityScreen.pushSuccessScreen`), les quêtes 1 et 2
 * de la réponse de `finalize`. Le serveur crédite les pétales à la montée d'étape, pas à la lecture :
 * sans cet appel, rien n'est payé deux fois, mais la fin suivante, dans la minute où le serveur ne l'a
 * pas encore remis à vrai, recompterait à l'écran les pétales de la montée d'avant, et la quête « tout
 * juste finie » se dirait encore.
 */
export function skillSetsToMarkRead(result: ApiFinalizeResult | null | undefined): number[] {
    return (result?.skill_sets ?? []).filter((ss) => ss?.id === 1 || ss?.id === 2).map((ss) => ss?.id as number)
}

/** Le même compte que l'ancien : une compétence à zéro dans son étape, dont l'évolution n'est pas lue. */
const justEvolved = (ss: ApiSkillSet | null | undefined) => ss?.current_count === 0 && !ss?.is_last_evolution_read

export function activityCongrats(result: ApiFinalizeResult | null | undefined): ActivityCongrats {
    const activity = result?.activity ?? {}
    const skillSets = result?.skill_sets ?? undefined

    const petals =
        skillSets
            ?.filter(justEvolved)
            .reduce((sum, ss) => sum + (ss?.steps?.[(ss?.current_step ?? 0) - 1]?.petals_gained as number), 0) || 0

    return {
        xp: activity.xp_gained ?? 0,
        petals,
        durationSeconds: activity.duration ?? 0,
    }
}

type CelebratingUser = {
    id?: number | null
    xp?: number | null
    activities?: { finished_at?: string | null }[] | null
}

/**
 * Le champion qui célèbre et son mot. `finishedToday` : les activités finies du jour, les plus
 * récentes d'abord (`getTodaysFinishedActivities`, `data/utility/dataUtils`).
 */
export function celebrationFor(
    user: CelebratingUser | null | undefined,
    finishedToday: { finished_at?: string | null }[],
    options: { champion?: CelebrationChampion; random?: () => number; today?: Date } = {}
): { champion: CelebrationChampion; title: string; subtitle: string | null } {
    const champion = options.champion ?? getChampion(user, options.today)
    const message = getCelebrationMessage(
        champion,
        finishedToday.length,
        user?.xp === 0,
        getIsFastCombo(finishedToday),
        options.random
    )
    return { champion, title: message?.title ?? "Bien joué !", subtitle: message?.subtitle ?? null }
}
